import {
  Box,
  Card,
  Group,
  Pagination,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { useState } from "react";
import type { BalanceHistory } from "../types";
import { IconFolderBolt } from "@tabler/icons-react";
import { RETURN_LABEL } from "../constants";

interface TransactionHistoryTableProps {
  balanceHistory: BalanceHistory[];
  membersList: {
    userId: string;
    email: string;
    role: string;
    firstName: string;
    lastName: string;
    status: string;
  }[];
}

export const TransactionHistoryTable: React.FC<
  TransactionHistoryTableProps
> = ({ balanceHistory, membersList }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(4);

  // Show empty state when there are no transactions
  if (balanceHistory.length === 0) {
    return (
      <Box mb="md">
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "3rem 1rem",
            textAlign: "center",
            border: "1px dashed #e0e0e0",
            borderRadius: "8px",
            marginTop: "1rem",
            backgroundColor: "#fafafa",
          }}
        >
          <IconFolderBolt
            size={48}
            color="#ccc"
            style={{ marginBottom: "1rem" }}
          />
          <Text size="lg" fw={500} c="dimmed" mb="xs">
            No Transaction History
          </Text>
          <Text size="sm" c="dimmed" maw={400}>
            Your {RETURN_LABEL} transaction history will appear here once you
            start using credits or making purchases.
          </Text>
        </Box>
      </Box>
    );
  }

  // Newest transactions first
  const sortedTransactions = [...balanceHistory].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  // Paginate transactions
  const paginatedTransactions = sortedTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handle items per page change
  const handleItemsPerPageChange = (value: string | null): void => {
    if (value !== null) {
      setItemsPerPage(Number(value));
      setCurrentPage(1);
    }
  };

  /**
   * Get the display name for a transaction's request user.
   * Uses the request_user_info from the backend if available,
   * otherwise falls back to the membersList lookup.
   */
  const getUserDisplayName = (transaction: BalanceHistory): string => {
    // Use request_user_info from backend if available
    if (transaction.requestUserInfo) {
      const { firstName, lastName, email } = transaction.requestUserInfo;
      if (firstName && lastName) {
        return `${firstName} ${lastName}`;
      }
      return email || "Unknown User";
    }

    // Fallback to looking up by userId (for backwards compatibility)
    const userId = transaction.requestUser;
    if (!userId) return "Unknown User";

    const member = membersList.find((member) => member.userId === userId);
    return member ? `${member.firstName} ${member.lastName}` : "Unknown User";
  };

  /**
   * Get the transaction description for display.
   * Shows client name for usage_consume transactions when available.
   */
  const getTransactionDescription = (
    transaction: BalanceHistory
  ): React.ReactNode => {
    if (transaction?.clientName) {
      // Show client name for usage_consume transactions
      return (
        <Text
          fz="12px"
          style={{ wordWrap: "break-word", overflowWrap: "break-word" }}
        >
          Client: {transaction.clientName}
        </Text>
      );
    } else if (transaction?.description) {
      return (
        <Text
          fz="12px"
          style={{ wordWrap: "break-word", overflowWrap: "break-word" }}
        >
          {transaction?.description}
        </Text>
      );
    } else {
      return (
        <Text
          fz="12px"
          style={{ wordWrap: "break-word", overflowWrap: "break-word" }}
        >
          No description available
        </Text>
      );
    }
  };

  return (
    <Box mb="md">
      <ScrollArea className="transaction-history-table">
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap={0}>
            <Text fw={500}>{RETURN_LABEL} History</Text>
            <Text size="xs">
              Review your {RETURN_LABEL} history and allocations
            </Text>
          </Stack>
          <Table
            verticalSpacing="xs"
            horizontalSpacing="md"
            styles={{
              th: { fontSize: "12px" },
              td: { fontSize: "12px" },
            }}
            mt="md"
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Date</Table.Th>
                <Table.Th
                  style={{
                    width: "250px",
                    maxWidth: "250px",
                  }}
                >
                  User
                </Table.Th>
                <Table.Th
                  style={{
                    width: "200px",
                    maxWidth: "200px",
                  }}
                >
                  Type
                </Table.Th>
                <Table.Th>Amount</Table.Th>
                <Table.Th
                  style={{
                    width: "400px",
                    maxWidth: "400px",
                  }}
                >
                  Details
                </Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {paginatedTransactions.map((transaction, index) => (
                <Table.Tr key={transaction.id || `transaction-${index}`}>
                  <Table.Td>
                    {new Date(transaction.createdAt).toLocaleDateString()}
                  </Table.Td>
                  <Table.Td
                    style={{
                      width: "150px",
                      maxWidth: "150px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <Tooltip label={getUserDisplayName(transaction)} withArrow>
                      <Text
                        fz="12px"
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        fw={600}
                      >
                        {getUserDisplayName(transaction)}
                      </Text>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td
                    style={{
                      width: "150px",
                      maxWidth: "150px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <Tooltip
                      label={
                        transaction.transactionTypeDisplay ||
                        transaction.transactionType.charAt(0).toUpperCase() +
                          transaction.transactionType.slice(1)
                      }
                      withArrow
                    >
                      <Text
                        fz="12px"
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {transaction.transactionTypeDisplay ||
                          transaction.transactionType.charAt(0).toUpperCase() +
                            transaction.transactionType.slice(1)}
                      </Text>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td
                    style={{
                      fontWeight: 600,
                      color: "#0077CC",
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {transaction.amount} {RETURN_LABEL}s
                  </Table.Td>
                  <Table.Td
                    style={{
                      width: "200px",
                      maxWidth: "200px",
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {getTransactionDescription(transaction)}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>

            <Table.Tfoot>
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Group
                    justify="space-between"
                    align="center"
                    mt="md"
                    fz="12px"
                  >
                    <Pagination
                      total={Math.ceil(
                        sortedTransactions.length / itemsPerPage
                      )}
                      value={currentPage}
                      onChange={setCurrentPage}
                      size="sm"
                      fz="12px"
                    />
                    <Select
                      value={itemsPerPage.toString()}
                      onChange={handleItemsPerPageChange}
                      data={[
                        { value: "4", label: "4 per page" },
                        { value: "8", label: "8 per page" },
                        { value: "12", label: "12 per page" },
                      ]}
                      size="xs"
                      fz="12px"
                    />
                  </Group>
                </Table.Td>
              </Table.Tr>
            </Table.Tfoot>
          </Table>
        </Card>
      </ScrollArea>
    </Box>
  );
};
